import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField
} from '@mui/material';
import { 
  Code as SchemaIcon,
  PlayArrow as PlayIcon,
  ContentCopy as CopyIcon,
  Science as ResearchIcon,
  AutoAwesome as MagicIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Bolt as BoltIcon
} from '@mui/icons-material';
import SchemaList from '../../components/schema/SchemaList';
import SchemaProcessor from '../../components/schema/SchemaProcessor';
import SchemaDetails from '../../components/schema/SchemaDetails';
import QueueStatus from '../../components/schema/QueueStatus';
import { schemaApiService } from '../../services/schemaApiService';
import { fetchWebsites, setSelectedWebsite } from '../../store/slices/websiteSlice';
import { getCountryFlag, cleanUrl } from '../../utils/countryFlags';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`schema-tabpanel-${index}`}
      aria-labelledby={`schema-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ px: 3, py: 3 }}>{children}</Box>}
    </div>
  );
}

const SchemaGeneratorPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const selectedWebsite = useSelector((state: RootState) => state.website.selectedWebsite);
  const websites = useSelector((state: RootState) => state.website.websites);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedSchema, setSelectedSchema] = useState<any>(null);
  const [queueStatus, setQueueStatus] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [singleUrl, setSingleUrl] = useState('');
  const [singleUrlLoading, setSingleUrlLoading] = useState(false);
  const [singleUrlSuccess, setSingleUrlSuccess] = useState<string | null>(null);
  const [refreshSchemaList, setRefreshSchemaList] = useState(0);

  // Load websites on component mount
  useEffect(() => {
    if (websites.length === 0) {
      dispatch(fetchWebsites());
    }
  }, [dispatch, websites.length]);

  useEffect(() => {
    if (selectedWebsite) {
      fetchQueueStatus();
    }
  }, [selectedWebsite]);

  const fetchQueueStatus = async () => {
    try {
      const status = await schemaApiService.getQueueStatus();
      setQueueStatus(status);
    } catch (err) {
      console.error('Error fetching queue status:', err);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSchemaSelect = async (schema: any) => {
    console.log('handleSchemaSelect called with:', schema);
    setLoading(true);
    try {
      // Fetch full schema details including FAQ opportunities and content gaps
      const response = await schemaApiService.getSchema(schema.id);
      console.log('API response:', response);
      if (response.success && response.data) {
        setSelectedSchema(response.data);
        setTabValue(2); // Switch to details tab
      } else {
        console.error('No data in response:', response);
        setError('No data received from server');
      }
    } catch (error) {
      console.error('Error fetching schema:', error);
      setError('Failed to load schema details: ' + (error as any).message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartBatchProcessing = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await schemaApiService.startBatchProcessing({ limit: 100 });
      
      if (result.success) {
        setError(null);
        fetchQueueStatus();
      } else {
        setError(result.error || 'Failed to start batch processing');
      }
    } catch (err: any) {
      setError(err.message || 'Error starting batch processing');
    } finally {
      setLoading(false);
    }
  };

  const handleSingleUrlSubmit = async () => {
    try {
      setSingleUrlLoading(true);
      setError(null);
      setSingleUrlSuccess(null);

      if (!singleUrl.trim()) {
        setError('Please enter a URL');
        return;
      }

      const result = await schemaApiService.generateSingleSchema(singleUrl);
      
      if (result.success) {
        setSingleUrlSuccess(`✨ Schema generated successfully for ${singleUrl}`);
        setSingleUrl('');
        fetchQueueStatus();
        // Trigger a refresh of the schema list
        setRefreshSchemaList(prev => prev + 1);
        // Switch to the schemas tab to show the newly generated schema
        setTabValue(0);
      } else {
        setError('Failed to generate schema');
      }
    } catch (err: any) {
      setError(err.message || 'Error generating schema');
    } finally {
      setSingleUrlLoading(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
            <MagicIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
              AI-Powered Schema Generator
            </Typography>
          </Box>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
            Generate structured data schemas for better search visibility
          </Typography>
        </Box>

        {/* Website Selector */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Select Website
          </Typography>
          <Autocomplete
            value={selectedWebsite}
            onChange={(event, newValue) => {
              if (newValue) {
                dispatch(setSelectedWebsite(newValue));
              }
            }}
            options={websites}
            getOptionLabel={(option) => {
              const flag = getCountryFlag(option.country);
              const domain = cleanUrl(option.base_url);
              return `${flag} ${domain}`;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Search for a website..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Typography>
                  {getCountryFlag(option.country)} {cleanUrl(option.base_url)}
                </Typography>
              </Box>
            )}
            sx={{ width: '100%' }}
            isOptionEqualToValue={(option, value) => option.website_id === value.website_id}
          />
        </Paper>

        {/* Quick Single URL Generation */}
        {selectedWebsite && (
          <Paper sx={{ 
            p: 3, 
            mb: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <BoltIcon />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Quick Schema Generation
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                value={singleUrl}
                onChange={(e) => setSingleUrl(e.target.value)}
                placeholder="Enter a URL to generate schema instantly (e.g., newyork.co.uk/nhl-ice-hockey-in-new-york)"
                disabled={singleUrlLoading}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSingleUrlSubmit();
                  }
                }}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: 1,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'white',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1,
                  },
                }}
              />
              <Button
                variant="contained"
                startIcon={singleUrlLoading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                onClick={handleSingleUrlSubmit}
                disabled={singleUrlLoading || !singleUrl.trim()}
                sx={{
                  backgroundColor: 'white',
                  color: '#667eea',
                  minWidth: '180px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              >
                {singleUrlLoading ? 'Generating...' : 'Generate Schema'}
              </Button>
            </Box>
          </Paper>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {singleUrlSuccess && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSingleUrlSuccess(null)}>
            {singleUrlSuccess}
          </Alert>
        )}

        {selectedWebsite ? (
          <>
            {/* Queue Status Overview */}
            <QueueStatus 
              status={queueStatus} 
              onRefresh={fetchQueueStatus}
              loading={loading}
            />

            {/* Main Content Tabs */}
            <Paper sx={{ mt: 3 }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="schema generator tabs">
                <Tab label="Generated Schemas" />
                <Tab label="Process New URLs" />
                <Tab label="Schema Details" disabled={!selectedSchema} />
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                <SchemaList 
                  onSchemaSelect={handleSchemaSelect} 
                  refreshTrigger={refreshSchemaList}
                />
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <SchemaProcessor onProcessComplete={fetchQueueStatus} />
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                {selectedSchema && (
                  <SchemaDetails 
                    schema={selectedSchema} 
                    onClose={() => {
                      setSelectedSchema(null);
                      setTabValue(0);
                    }}
                  />
                )}
              </TabPanel>
            </Paper>
          </>
        ) : (
          <Alert severity="info">
            Please select a website from the dropdown above to start generating schemas.
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default SchemaGeneratorPage;